import { DocumentDirection, DocumentType } from '@repo/database/browser';
import { findMapValue } from '@repo/utils';
import {
  ADMINISTRATION,
  DOCUMENTS,
  INVOICES,
  DELIVERY_NOTES,
  ORDERS,
  PROTECTED,
} from '@/lib/constants/routes';

export type DocumentHubKey =
  | 'invoices'
  | 'delivery-notes'
  | 'orders'
  | 'administration';

/**
 * Union di tutti i `subTab.value` definiti in `DOCUMENT_HUBS`. Centralizzato
 * qui per type-safety sulle comparison letterali di `filterSubTab`.
 * Mantieni allineato con i value usati in `DOCUMENT_HUBS` sotto.
 */
export type DocumentSubTabValue =
  | 'outgoing'
  | 'incoming'
  | 'proforma'
  | 'quotes'
  | 'ddt-outgoing'
  | 'ddt-incoming'
  | 'orders-outgoing'
  | 'orders-incoming'
  | 'payroll'
  | 'paObligations'
  | 'debts';

export interface DocumentHubConfig {
  key: DocumentHubKey;
  basePath: string;
  titleI18nKey: string;
  subTabs: readonly DocumentSubTab[];
  defaultSubTab: DocumentSubTabValue;
}

interface DocumentSubTab {
  value: DocumentSubTabValue;
  i18nKey: string;
}

export const DOCUMENT_HUBS: Record<DocumentHubKey, DocumentHubConfig> = {
  invoices: {
    key: 'invoices',
    basePath: `${PROTECTED}${INVOICES}`,
    titleI18nKey: 'sidebar.invoices',
    subTabs: [
      { value: 'outgoing', i18nKey: 'document.tabs.outgoing' },
      { value: 'incoming', i18nKey: 'document.tabs.incoming' },
      { value: 'proforma', i18nKey: 'document.tabs.proforma' },
      { value: 'quotes', i18nKey: 'document.tabs.quotes' },
    ],
    defaultSubTab: 'outgoing',
  },
  'delivery-notes': {
    key: 'delivery-notes',
    basePath: `${PROTECTED}${DELIVERY_NOTES}`,
    titleI18nKey: 'sidebar.delivery_notes',
    subTabs: [
      { value: 'ddt-outgoing', i18nKey: 'document.tabs.ddt_outgoing' },
      { value: 'ddt-incoming', i18nKey: 'document.tabs.ddt_incoming' },
    ],
    defaultSubTab: 'ddt-outgoing',
  },
  orders: {
    key: 'orders',
    basePath: `${PROTECTED}${ORDERS}`,
    titleI18nKey: 'sidebar.orders',
    subTabs: [
      { value: 'orders-outgoing', i18nKey: 'document.tabs.orders_outgoing' },
      { value: 'orders-incoming', i18nKey: 'document.tabs.orders_incoming' },
    ],
    defaultSubTab: 'orders-outgoing',
  },
  administration: {
    key: 'administration',
    basePath: `${PROTECTED}${ADMINISTRATION}`,
    titleI18nKey: 'sidebar.administration',
    subTabs: [
      { value: 'payroll', i18nKey: 'document.tabs.payroll' },
      { value: 'paObligations', i18nKey: 'document.tabs.paObligations' },
      { value: 'debts', i18nKey: 'document.tabs.debts' },
    ],
    defaultSubTab: 'payroll',
  },
};

const LEGACY_SUBTAB_TO_HUB: Record<DocumentSubTabValue, DocumentHubKey> = {
  incoming: 'invoices',
  outgoing: 'invoices',
  proforma: 'invoices',
  quotes: 'invoices',
  'ddt-outgoing': 'delivery-notes',
  'ddt-incoming': 'delivery-notes',
  'orders-outgoing': 'orders',
  'orders-incoming': 'orders',
  payroll: 'administration',
  paObligations: 'administration',
  debts: 'administration',
};

export function resolveHubForLegacySubTab(
  subTab: string | undefined
): DocumentHubConfig | null {
  const key = findMapValue(LEGACY_SUBTAB_TO_HUB, subTab);
  return key ? DOCUMENT_HUBS[key] : null;
}

export function getDocumentLegacyBasePath(): string {
  return `${PROTECTED}${DOCUMENTS}`;
}

/**
 * Deriva il `subTab` corretto per un documento esistente dal suo `type` +
 * `direction`. È l'unico modo affidabile di scegliere il form quando l'URL non
 * porta `subTab` (es. apertura dal pannello commessa o dalla tabella prima del
 * click su un tab): il dispatcher e gli entry point non possono fidarsi
 * dell'URL perché `type` da solo non discrimina vendita da acquisto.
 *
 * Ritorna `undefined` per i tipi senza hub dedicato (`RECEIPT`,
 * `EXPENSE_REPORT`, ...): il chiamante ricade sul form legacy, invariato.
 */
export function resolveSubTabForDocument(
  document?: { type: DocumentType; direction: DocumentDirection } | null
): DocumentSubTabValue | undefined {
  if (!document) return undefined;
  const isOutgoing = document.direction === DocumentDirection.OUTGOING;
  switch (document.type) {
    case DocumentType.INVOICE:
      return isOutgoing ? 'outgoing' : 'incoming';
    case DocumentType.DELIVERY_NOTE:
      return isOutgoing ? 'ddt-outgoing' : 'ddt-incoming';
    case DocumentType.ORDER:
      return isOutgoing ? 'orders-outgoing' : 'orders-incoming';
    case DocumentType.QUOTE:
      return 'quotes';
    case DocumentType.PROFORMA:
      return 'proforma';
    case DocumentType.PAYROLL:
      return 'payroll';
    case DocumentType.TAX_PAYMENT:
    case DocumentType.INPS_CONTRIBUTION:
    case DocumentType.INAIL_PREMIUM:
    case DocumentType.PA_DEBT:
      return 'paObligations';
    case DocumentType.LOAN:
      return 'debts';
    default:
      return undefined;
  }
}
